// Copyright (c) Docugami, Inc. All rights reserved.

import { Icon, IconButton, IContextualMenuItem, OverflowSet, TooltipHost } from "@fluentui/react";
import { appContentColor, backgroundGray, greyText, primaryTheme, themeBlue, themeRedDark } from "components/shell/presentation/AppStyles";
import { DgIconButton } from "components/shell/utils/DgIconButton";
import { dgIconNames } from "hooks/dgIcons";
import { Ref, forwardRef, useMemo } from "react";
import { ConnectDragSource } from "react-dnd";
import styled from "styled-components";
import { AutomationId, AutomationProps } from "utils/Automation";
import { CardContainer, GrabHandleContainer, ItemCardletStyles, getClassNames } from "./CardStyles";
import { ConfirmationCommandButton, IConfirmationCommandButton, IconAlignment } from "./CommandButton";

const ContentRoot = styled.div`
    display: flex;
    flex-direction: column;
    height: 100%;
    overflow: hidden;
    padding-bottom: 4px;
    >:first-child {
        border-bottom: 1px solid ${backgroundGray};
    }
    >div {
        padding: 4px 7px 0px 7px;
        box-sizing: border-box;
    }
`

const IconContainer = styled.div`
    display: flex;
    gap: 8px;
`

const Header = styled.div`
    display: flex;
    flex-direction: row;
    width: 100%;
    gap: 5px;
`

const LabelContainer = styled.div`
    flex: 1;
    margin-left: 10px;
    padding-bottom: 5px;
`

const Body = styled.div`
    flex: 1;
    width: 100%;
    max-height: 100%;
    overflow: auto;
`

const Footer = styled.div`
    display: flex;
    width: 100%;
`

export interface ItemCardletProps extends AutomationProps {
    header?: JSX.Element;
    children?: JSX.Element | JSX.Element[];
    footer?: JSX.Element;
    collapsed?: boolean;
    dragHandleRef?: ConnectDragSource;
    onRemove?: () => void;
    canRemove?: boolean;
    styles?: ItemCardletStyles;
    onExamplesClick?: () => void;
    overflowCommands?: IContextualMenuItem[];
}

export const ItemCardlet = forwardRef((props: ItemCardletProps, ref?: Ref<HTMLDivElement>): JSX.Element => {
    if (props.collapsed) {
        return (
            <CardContainer
                ref={ref}
                data-automation-id={props.automationId}
                className={getClassNames(props.styles?.root)}>
                <ContentRoot>
                    <CardletHeader
                        onRemove={props.onRemove}
                        canRemove={props.canRemove}
                        label={props.header}
                        dragHandleRef={props.dragHandleRef} />
                </ContentRoot>
            </CardContainer>
        )
    }

    return (
        <CardContainer
            ref={ref}
            className={getClassNames(props.styles?.root)}
            data-automation-id={props.automationId}>
            <ContentRoot>
                <CardletHeader
                    onRemove={props.onRemove}
                    canRemove={props.canRemove}
                    label={props.header}
                    dragHandleRef={props.dragHandleRef}
                    onExamplesClick={props.onExamplesClick}
                    overflowCommands={props.overflowCommands} />
                <Body>
                    {props.children}
                </Body>
                {!!props.footer && <Footer>
                    {props.footer}
                </Footer>}
            </ContentRoot>
        </CardContainer>
    )
})

interface CardletHeaderProps {
    label?: JSX.Element,
    dragHandleRef?: ConnectDragSource,
    onRemove?: () => void,
    canRemove?: boolean,
    onExamplesClick?: () => void;
    overflowCommands?: IContextualMenuItem[];
}

export const CardletHeader = (props: CardletHeaderProps): JSX.Element => {
    const removeCommand: IConfirmationCommandButton = useMemo(() => {
        return {
            icon: {
                name: dgIconNames.DgTrashCan,
                width: 26,
                height: 30,
                defaultColor: greyText
            },
            label: 'remove',
            canExecute: !!props.canRemove,
            onClick: props.onRemove,
            confirmationLabel: 'Remove?',
            confirmationColor: primaryTheme.palette.white,
            confirmationBackgroundColor: themeRedDark
        }
    }, [props]);

    function onRenderOverflowButton(overflowItems?: any[]): JSX.Element | null {
        if (!overflowItems) {
            return null;
        }

        return (
            <IconButton
                iconProps={{ iconName: "More" }}
                styles={{
                    root: {
                        "i": {
                            fontWeight: 700
                        }
                    },
                    icon: {
                        fontWeight: 700,
                        color: appContentColor
                    },
                }}
                menuProps={{ items: overflowItems, styles: { root: { backgroundColor: primaryTheme.palette.white } } }}
                onRenderMenuIcon={() => null} />
        )
    }

    return (
        <Header>
            <GrabHandleContainer ref={props.dragHandleRef} disabled={!props.dragHandleRef}>
                <Icon iconName="CalculatorEqualTo" />
            </GrabHandleContainer>
            <LabelContainer>
                {props.label}
            </LabelContainer>
            {
                !!props.overflowCommands?.length && (
                    <OverflowSet
                        overflowItems={props.overflowCommands}
                        onRenderItem={() => null}
                        onRenderOverflowButton={onRenderOverflowButton} />
                )
            }
            <IconContainer>
                {
                    !!props.onExamplesClick &&
                    <TooltipHost content='View examples'>
                        <DgIconButton
                            iconName={dgIconNames.DgExampleChunks}
                            height={13}
                            width={18}
                            color={themeBlue}
                            onClick={props.onExamplesClick}
                            label='' />
                    </TooltipHost>

                }
                {
                    !!props.onRemove &&
                    <ConfirmationCommandButton
                        command={removeCommand}
                        width={14}
                        height={14}
                        iconAlignment={IconAlignment.Left}
                        dataAutomationId={AutomationId.RemoveEntryConfirmationButton} />
                }
            </IconContainer>
        </Header>
    );
}
